'use client';

import { useLanguage } from '@/context/LanguageContext';
import { Building2, Landmark, Globe } from 'lucide-react';
import Reveal, { StaggerChildren, StaggerItem } from './Reveal';

const audiences = [
  {
    icon: Building2,
    gradient: 'from-electric/20 to-cyan/10',
    iconColor: 'text-electric',
    fr: {
      title: 'PME & TPE',
      desc: "Structures de 5 à 30 employés, sans équipe IT interne, qui ont besoin d\u2019un partenaire fiable pour gérer leur environnement numérique.",
    },
    en: {
      title: 'SMEs & Small Businesses',
      desc: 'Organizations of 5 to 30 employees, without internal IT staff, who need a reliable partner to manage their digital environment.',
    },
  },
  {
    icon: Landmark,
    gradient: 'from-violet-500/20 to-electric/10',
    iconColor: 'text-violet-400',
    fr: {
      title: 'Institutions',
      desc: 'Associations et organisations avec des exigences de fiabilité, de sécurité et de conformité élevées.',
    },
    en: {
      title: 'Institutions',
      desc: 'Associations, and organizations with high reliability, security, and compliance requirements.',
    },
  },
  {
    icon: Globe,
    gradient: 'from-cyan/20 to-emerald-400/10',
    iconColor: 'text-cyan',
    fr: {
      title: 'Équipes internationales',
      desc: 'Environnements bilingues ou multilingues nécessitant un partenaire IT flexible et capable de communiquer dans plusieurs langues.',
    },
    en: {
      title: 'International Teams',
      desc: 'Bilingual or multilingual environments requiring a flexible IT partner capable of communicating in multiple languages.',
    },
  },
];

export default function Audience() {
  const { lang, t } = useLanguage();

  return (
    <section className="relative py-20 md:py-28 bg-dark-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm font-semibold text-cyan tracking-widest uppercase mb-3">
              {t('Nos clients', 'Our clients')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {t('Pour qui travaillons-nous', 'Who we work with')}
            </h2>
            <p className="mt-4 text-lg text-white/50">
              {t(
                'Des organisations qui partagent un besoin commun : un IT fiable, sans complexité.',
                'Organizations that share a common need: reliable IT, without complexity.'
              )}
            </p>
          </div>
        </Reveal>

        <StaggerChildren className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {audiences.map((audience, i) => {
            const Icon = audience.icon;
            const content = lang === 'fr' ? audience.fr : audience.en;
            return (
              <StaggerItem key={i}>
                <div className="relative text-center p-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm hover:bg-white/[0.05] hover:border-white/[0.12] transition-all duration-500 h-full overflow-hidden group">
                  {/* Gradient glow behind icon */}
                  <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full bg-gradient-to-b ${audience.gradient} blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none`} />

                  <div className={`relative w-14 h-14 rounded-2xl bg-white/[0.05] border border-white/[0.08] flex items-center justify-center ${audience.iconColor} mx-auto mb-5`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="relative text-lg font-semibold text-white mb-3">
                    {content.title}
                  </h3>
                  <p className="relative text-white/55 leading-relaxed">
                    {content.desc}
                  </p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerChildren>
      </div>
    </section>
  );
}
